/**
 * +prof: the two gathering professions, Foraging and Mining (docs/design/professions.md): the node
 * kinds (names, tiers, skill requirements, channel and respawn times, mesh look) and what each
 * gather yields into the existing Altar Forge economy. Pure data; placement is mossvaleNodes.ts,
 * the sim channels and depletes nodes (sim field/gather.ts), progression pays the gather.
 */
import type { GatherProfession, Ms } from '@keystone/sim';

export type { GatherProfession } from '@keystone/sim';

export const PROFESSIONS: readonly GatherProfession[] = ['foraging', 'mining'];
export const PROFESSION_NAME: Readonly<Record<GatherProfession, string>> = { foraging: 'Foraging', mining: 'Mining' };

/** Skill cap in Mossvale (later zones raise it with new tiers). */
export const PROF_SKILL_CAP = 100;

/** What a gather may pay. `scale` = a Coil Scale of `grade`; gold is herb value sold on the spot. */
export type GatherResource = 'gold' | 'fangsteel' | 'scale' | 'venom' | 'moltskin';
export interface GatherYield {
  resource: GatherResource;
  /** Chance per gather (0..1). */
  chance: number;
  min: number;
  max: number;
  /** Scales only: the grade ('ashen' | 'jade' | ...). */
  grade?: 'ashen' | 'jade' | 'gilded' | 'eternal';
  /** A rare drop (the reward pop and toast call it out). */
  rare?: boolean;
}

/** One tier of nodes: requirement, XP level, channel, respawn, bonus-yield chance. */
export interface GatherTierDef {
  tier: number;
  /** Minimum skill to gather (the tooltip's "Requires Foraging 40"). */
  req: number;
  /** Level the character XP is sized at (killXp(level, hero) × colour units). */
  level: number;
  channelMs: Ms;
  respawnMs: readonly [Ms, Ms];
  /** Chance of a bonus yield ("Rich vein" / "Lush patch": everything the gather pays doubles). */
  bonusChance: number;
}

/**
 * Tiers match the zone's level bands (docs/design/professions.md §2): T1 Sunmeadow (1–3),
 * T2 Barrowfield and Glimmermere (3–5), T3 Greyhorn and the forest edge (5–8), T4 the Thornheart,
 * the Pale Rise, cliff ledges and the mine's depths (8–10).
 */
export const GATHER_TIERS: readonly GatherTierDef[] = [
  { tier: 1, req: 1, level: 2, channelMs: 1600, respawnMs: [180_000, 240_000], bonusChance: 0 },
  { tier: 2, req: 15, level: 4, channelMs: 2000, respawnMs: [195_000, 255_000], bonusChance: 0.05 },
  { tier: 3, req: 35, level: 6, channelMs: 2400, respawnMs: [210_000, 270_000], bonusChance: 0.08 },
  { tier: 4, req: 60, level: 9, channelMs: 2800, respawnMs: [240_000, 300_000], bonusChance: 0.12 },
];
export const gatherTier = (tier: number): GatherTierDef => GATHER_TIERS[Math.max(0, Math.min(GATHER_TIERS.length - 1, tier - 1))]!;

/** A node kind: name, profession, tier, the renderer's look and its yields (every gather pays a little gold; the rest are chances). */
export interface GatherNodeKind {
  id: string;
  name: string;
  profession: GatherProfession;
  tier: number;
  /** One line for the tooltip. */
  flavor: string;
  /** Renderer mesh family and colours (render/toon/outdoor/nodes.ts). */
  look: { mesh: 'leafy' | 'bell' | 'mushroom' | 'star' | 'outcrop' | 'seam' | 'vein' | 'lode'; color: string; accent: string };
  yields: readonly GatherYield[];
}

export const GATHER_NODE_KINDS: readonly GatherNodeKind[] = [
  // ---- Foraging ---------------------------------------------------------------------------
  {
    id: 'sunleaf',
    name: 'Sunleaf',
    profession: 'foraging',
    tier: 1,
    flavor: 'A sprig of golden meadow leaf. The town apothecary pays a few coins a bundle.',
    look: { mesh: 'leafy', color: '#9fd24a', accent: '#ffd85a' },
    yields: [{ resource: 'gold', chance: 1, min: 3, max: 5 }],
  },
  {
    id: 'marshbell',
    name: 'Marshbell',
    profession: 'foraging',
    tier: 2,
    flavor: 'Pale blue bells that grow where the ground stays wet.',
    look: { mesh: 'bell', color: '#5fae7a', accent: '#8fd0ff' },
    yields: [
      { resource: 'gold', chance: 1, min: 5, max: 7 },
      { resource: 'venom', chance: 0.02, min: 1, max: 1, rare: true },
    ],
  },
  {
    id: 'bloomcap',
    name: 'Bloomcap',
    profession: 'foraging',
    tier: 3,
    flavor: 'A cluster of glowing caps. Their sap is the base of a viper-venom draught.',
    look: { mesh: 'mushroom', color: '#c9643f', accent: '#ffb36b' },
    yields: [
      { resource: 'gold', chance: 1, min: 7, max: 10 },
      { resource: 'venom', chance: 0.05, min: 1, max: 1 },
      { resource: 'moltskin', chance: 0.015, min: 1, max: 1, rare: true },
    ],
  },
  {
    id: 'starpetal',
    name: 'Starpetal',
    profession: 'foraging',
    tier: 4,
    flavor: 'A silver flower that only opens where starfall once struck. Prized by alchemists.',
    look: { mesh: 'star', color: '#cfd8ff', accent: '#b98cff' },
    yields: [
      { resource: 'gold', chance: 1, min: 10, max: 13 },
      { resource: 'venom', chance: 0.08, min: 1, max: 1 },
      { resource: 'moltskin', chance: 0.03, min: 1, max: 1, rare: true },
    ],
  },
  // ---- Mining -------------------------------------------------------------------------------
  {
    id: 'rustvein',
    name: 'Rustvein Outcrop',
    profession: 'mining',
    tier: 1,
    flavor: 'Rust-streaked stone with a little green-black metal in it.',
    look: { mesh: 'outcrop', color: '#8a7a68', accent: '#c4703a' },
    yields: [
      { resource: 'gold', chance: 1, min: 1, max: 2 },
      { resource: 'fangsteel', chance: 0.25, min: 1, max: 1 },
      { resource: 'scale', chance: 0.05, min: 1, max: 1, grade: 'ashen' },
    ],
  },
  {
    id: 'tallow_seam',
    name: 'Tallow Seam',
    profession: 'mining',
    tier: 2,
    flavor: 'A waxy yellow seam. Tallowdeep was dug after one of these.',
    look: { mesh: 'seam', color: '#7d7466', accent: '#f0d27a' },
    yields: [
      { resource: 'gold', chance: 1, min: 1, max: 3 },
      { resource: 'fangsteel', chance: 0.35, min: 1, max: 1 },
      { resource: 'scale', chance: 0.06, min: 1, max: 1, grade: 'ashen' },
    ],
  },
  {
    id: 'ashen_vein',
    name: 'Ashen Vein',
    profession: 'mining',
    tier: 3,
    flavor: 'Grey ore that flakes like shed scales. Coil Scales sometimes come out whole.',
    look: { mesh: 'vein', color: '#5d5e66', accent: '#cfcfd8' },
    yields: [
      { resource: 'gold', chance: 1, min: 2, max: 4 },
      { resource: 'fangsteel', chance: 0.4, min: 1, max: 1 },
      { resource: 'scale', chance: 0.08, min: 1, max: 1, grade: 'ashen' },
    ],
  },
  {
    id: 'coilstone_lode',
    name: 'Coilstone Lode',
    profession: 'mining',
    tier: 4,
    flavor: 'Coiled bands of green-black ore. The richest metal in Mossvale.',
    look: { mesh: 'lode', color: '#2f4a3c', accent: '#62e0a0' },
    yields: [
      { resource: 'gold', chance: 1, min: 3, max: 5 },
      { resource: 'fangsteel', chance: 0.45, min: 1, max: 2 },
      { resource: 'scale', chance: 0.1, min: 1, max: 1, grade: 'ashen' },
      { resource: 'scale', chance: 0.015, min: 1, max: 1, grade: 'jade', rare: true },
    ],
  },
];

const BY_ID = new Map(GATHER_NODE_KINDS.map((k) => [k.id, k]));
export const gatherNodeKind = (id: string): GatherNodeKind | undefined => BY_ID.get(id);
/** The kind of a profession at a tier. */
export const gatherKindAt = (prof: GatherProfession, tier: number): GatherNodeKind => GATHER_NODE_KINDS.find((k) => k.profession === prof && k.tier === tier)!;

// ---------------------------------------------------------------------------
// Skill colours and skill-ups (WoW-style)
// ---------------------------------------------------------------------------

export type GatherColor = 'red' | 'orange' | 'yellow' | 'green' | 'grey';

/**
 * Colour of a node for a skill: red below its requirement (can't gather), then by how far above it
 * the skill is: orange < +20, yellow < +40, green < +60, grey beyond.
 */
export const GATHER_COLOR_BANDS = { orange: 20, yellow: 40, green: 60 } as const;

export function gatherColor(skill: number, req: number): GatherColor {
  const d = Math.floor(skill) - req;
  if (d < 0) return 'red';
  if (d < GATHER_COLOR_BANDS.orange) return 'orange';
  if (d < GATHER_COLOR_BANDS.yellow) return 'yellow';
  if (d < GATHER_COLOR_BANDS.green) return 'green';
  return 'grey';
}

/** Skill-up per colour: the chance of one and how many points it gives. */
export const SKILL_UP: Readonly<Record<GatherColor, { chance: number; points: number }>> = {
  red: { chance: 0, points: 0 },
  orange: { chance: 1, points: 3 },
  yellow: { chance: 0.75, points: 2 },
  green: { chance: 0.35, points: 1 },
  grey: { chance: 0, points: 0 },
};

/** Character XP per gather, in kill-units at the tier's level (the level-gap penalty applies on top). */
export const GATHER_XP_UNITS: Readonly<Record<GatherColor, number>> = { red: 0, orange: 1, yellow: 1, green: 0.75, grey: 0.2 };

/** CSS colour of a skill colour (tooltips, the panel). */
export const GATHER_COLOR_CSS: Readonly<Record<GatherColor, string>> = { red: '#ff4040', orange: '#ff8040', yellow: '#ffff00', green: '#40c040', grey: '#9d9d9d' };
